



import xss from 'xss';
import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkifyjs/html';
import hashtag from 'linkifyjs/plugins/hashtag';

hashtag(linkify);

const unicodeHashtagPattern = /(^|[^\p{L}\p{M}\p{N}_])#([\p{L}\p{M}\p{N}_]+)/gu;

// Linkify hashtags ourselves because linkifyjs 2.x stops at non-ASCII letters such as German umlauts.
const linkifyUnicodeHashtags = (html: string) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node = walker.nextNode();
    while (node) {
        textNodes.push(node as Text);
        node = walker.nextNode();
    }

    textNodes.forEach((textNode) => {
        if (textNode.parentElement?.closest('a, span')) return;
        unicodeHashtagPattern.lastIndex = 0;
        if (!unicodeHashtagPattern.test(textNode.data)) return;

        unicodeHashtagPattern.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        textNode.data.replace(unicodeHashtagPattern, (match, prefix, label, offset) => {
            fragment.append(document.createTextNode(textNode.data.slice(lastIndex, offset)));
            if (prefix) fragment.append(document.createTextNode(prefix));

            const hashtagElement = document.createElement('span');
            hashtagElement.dataset.type = 'cohashtag';
            hashtagElement.dataset.id = label;
            hashtagElement.textContent = `#${label}`;
            fragment.append(hashtagElement);
            lastIndex = offset + match.length;
            return match;
        });
        fragment.append(document.createTextNode(textNode.data.slice(lastIndex)));
        textNode.replaceWith(fragment);
    });

    return template.innerHTML;
};

export default {
    name: 'CoHtml',
    props: {
        html: {
            type: String,
            default: '',
        },
        // this replaces the linkifytiptap directive
        linkify: {
            type: Boolean,
            default: true,
        },
        // xssOptions is used to sanitize the html input
        xssOptions: {
            type: Object,
            default: () => ({
                // see https://jsxss.com/en/options.html
                // preserve span tags with attributes data-type="mention" and data-id=""
                whiteList: {
                    a: ['href', 'target', 'rel'],
                    b: [],
                    i: [],
                    em: [],
                    s: [],
                    strong: [],
                    p: [],
                    br: [],
                    ul: [],
                    ol: [],
                    li: [],
                    span: ['data-type', 'data-id'], // allow data-type and data-id attributes on span tags to preserve mentions and hashtags
                    h1: [],
                    h2: [],
                    h3: [],
                    h4: [],
                    h5: [],
                    h6: [],
                    pre: [],
                    code: [],
                },
                // strip out all tags except those in the whiteList
                stripIgnoreTag: true,
                // remove the content of script tags
                stripIgnoreTagBody: ['script'],
            }),
        },
    },
    computed: {
        sanitizedHtml() {
            if (!this.html) return '';
            // Sanitize the HTML input
            let cleanHtml = xss(this.html, this.xssOptions);
            // If linkify is enabled, convert URLs, emails, and hashtags to links
            if (this.linkify) {
                // linkifyHtml will convert URLs and emails to links.
                cleanHtml = linkifyHtml(cleanHtml, {
                    defaultProtocol: 'https',
                    className: 'linkified-co-html',
                    ignoreTags: ['span'], // ignore span hashtags to prevent double linking of mentions and hashtags
                    validate: {
                        // prevent linking of mentions
                        mention() {
                            return false;
                        },
                        // Unicode-aware hashtag handling runs below after URLs and emails are linked.
                        hashtag() {
                            return false;
                        },
                    },
                });
                cleanHtml = linkifyUnicodeHashtags(cleanHtml);
            }
            return cleanHtml;
        },
    },
    mounted() {
        // add linkhandler to hashtags and mentions
        this.$nextTick(() => {
            this.addClickHandlers();
        });
    },
    methods: {
        // This method is used to handle the click event on links
        addClickHandlers() {
            const mentions = this.$el?.querySelectorAll ? this.$el.querySelectorAll('span[data-type="mention"]') : [];
            mentions.forEach((mention) => {
                if (!mention.dataset?.id) return;
                mention.addEventListener('click', () => {
                    this.$router?.push({
                        path: `/profile/${mention.dataset.id}`,
                    });
                });
            });
            const hashtags = this.$el.querySelectorAll('span[data-type*="hashtag"]');
            hashtags.forEach((tag) => {
                if (!tag.dataset?.id) return;
                tag.addEventListener('click', () => {
                    this.$router?.push({
                        path: `/search`,
                        query: {
                            term: tag.dataset.id,
                            type: 'hashtag',
                        },
                    });
                });
            });
        },
    },
};
